import { useEffect, useMemo, useState } from "react";
import { Pagination, pageSlice } from "../components/Pagination";
import { Link } from "@tanstack/react-router";
import {
  Loader2,
  FileText,
  Plus,
  FileDown,
  CheckCircle2,
  XCircle,
  Trash2,
  Send,
  Mail,
  Edit3,
  Undo2,
  Repeat,
} from "lucide-react";
import {
  useFacturas,
  useMarkFacturaPaid,
  useVoidFactura,
  useRectifyFactura,
  useMakeRecurring,
  useDeleteFactura,
  useSetFacturaDueDate,
  useResendVerifacti,
  useSendFacturaEmail,
  openFacturaPdf,
} from "../api/facturas";
import { useIssuers } from "../api/issuers";
import { useClientes } from "../api/clientes";
import type { FacturaEstado, FacturaListFilters } from "../types/factura";

const estadoBadge: Record<FacturaEstado, string> = {
  borrador: "bg-slate-100 text-slate-700 border-slate-200",
  emitida: "bg-sky-50 text-sky-700 border-sky-200",
  pagada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  anulada: "bg-rose-50 text-rose-700 border-rose-200",
};

function formatEUR(n: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

export function FacturasListPage() {
  const [filters, setFilters] = useState<FacturaListFilters>({});
  const { data: facturas, isLoading, error } = useFacturas(filters);
  const { data: emisores } = useIssuers();
  const { data: clientes } = useClientes(filters.emisor_id);

  const markPaid = useMarkFacturaPaid();
  const voidFactura = useVoidFactura();
  const rectify = useRectifyFactura();
  const makeRecurring = useMakeRecurring();
  const deleteFactura = useDeleteFactura();
  const setDueDate = useSetFacturaDueDate();
  const resendVerifacti = useResendVerifacti();
  const sendEmail = useSendFacturaEmail();

  const [editingDueId, setEditingDueId] = useState<number | null>(null);
  const [editingDueValue, setEditingDueValue] = useState<string>("");

  // ── Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Reset a la página 1 cuando cambian los filtros
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const total = useMemo(
    () => (facturas ?? []).reduce((acc, f) => acc + f.total, 0),
    [facturas],
  );

  const pagedFacturas = useMemo(
    () => pageSlice(facturas, page, pageSize),
    [facturas, page, pageSize],
  );

  function clearFilters() {
    setFilters({});
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <FileText size={28} className="text-brand-700" /> Facturación
          </h1>
          <p className="text-slate-500 mt-1">
            Listado de facturas emitidas. {facturas && (
              <span className="font-semibold text-slate-700">
                {facturas.length} {facturas.length === 1 ? "factura" : "facturas"} · Total {formatEUR(total)}
              </span>
            )}
          </p>
        </div>
        <Link
          to="/billing/invoices/new"
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
        >
          <Plus size={18} /> Nueva factura
        </Link>
      </header>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-4 shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Emisor
            </label>
            <select
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
              value={filters.emisor_id ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  emisor_id: e.target.value ? Number(e.target.value) : undefined,
                  cliente_id: undefined,
                }))
              }
            >
              <option value="">Todos</option>
              {emisores?.map((em) => (
                <option key={em.id} value={em.id}>
                  {em.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Cliente
            </label>
            <select
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
              value={filters.cliente_id ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  cliente_id: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
            >
              <option value="">Todos</option>
              {clientes?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Estado
            </label>
            <select
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
              value={filters.estado ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  estado: (e.target.value || undefined) as FacturaEstado,
                }))
              }
            >
              <option value="">Todos</option>
              <option value="emitida">Emitida</option>
              <option value="pagada">Pagada</option>
              <option value="anulada">Anulada</option>
              <option value="borrador">Borrador</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Nº pedido
            </label>
            <input
              type="text"
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
              value={filters.numero_pedido ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  numero_pedido: e.target.value || undefined,
                }))
              }
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Total mín / máx
            </label>
            <div className="flex gap-2 mt-1">
              <input
                type="number"
                placeholder="mín"
                className="w-1/2 rounded-lg border border-slate-300 px-2 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
                value={filters.total_min ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    total_min: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
              <input
                type="number"
                placeholder="máx"
                className="w-1/2 rounded-lg border border-slate-300 px-2 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
                value={filters.total_max ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    total_max: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Vencimiento
            </label>
            <div className="flex gap-2 mt-1">
              <input
                type="date"
                className="w-1/2 rounded-lg border border-slate-300 px-2 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
                value={filters.vencimiento_desde ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    vencimiento_desde: e.target.value || undefined,
                  }))
                }
              />
              <input
                type="date"
                className="w-1/2 rounded-lg border border-slate-300 px-2 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
                value={filters.vencimiento_hasta ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    vencimiento_hasta: e.target.value || undefined,
                  }))
                }
              />
            </div>
          </div>
        </div>

        {Object.keys(filters).length > 0 && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-xs font-bold text-slate-600 hover:text-slate-900"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          Error al cargar las facturas.
        </div>
      )}

      {facturas && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-340px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Nº</th>
                  <th className="text-left px-4 py-3 font-bold">Fecha</th>
                  <th className="text-left px-4 py-3 font-bold">Emisor</th>
                  <th className="text-left px-4 py-3 font-bold">Cliente</th>
                  <th className="text-right px-4 py-3 font-bold">Total</th>
                  <th className="text-left px-4 py-3 font-bold">Nº pedido</th>
                  <th className="text-left px-4 py-3 font-bold">Estado</th>
                  <th className="text-left px-4 py-3 font-bold">Vencimiento</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {facturas.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                      No hay facturas que coincidan con los filtros.
                    </td>
                  </tr>
                )}
                {pagedFacturas.map((f) => (
                <tr
                  key={f.id}
                  className="border-t border-slate-100 hover:bg-slate-50/60 transition"
                >
                  <td className="px-4 py-3 font-mono font-bold text-slate-900">
                    {f.numero}
                    {f.tipo === "rectificativa" && (
                      <span className="ml-2 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-black text-rose-600 align-middle">
                        RECT.
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {f.fecha && new Date(f.fecha).toLocaleDateString("es-ES")}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center gap-2 text-sm font-bold"
                      style={{ color: f.emisor?.color_hex ?? undefined }}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{
                          background: f.emisor?.color_hex ?? "#94a3b8",
                        }}
                      />
                      {f.emisor?.nombre ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-bold text-slate-900">
                      {f.cliente?.nombre ?? "—"}
                    </div>
                    {f.cliente?.cif && (
                      <div className="text-xs text-slate-500 font-mono">
                        {f.cliente.cif}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-slate-900">
                    {formatEUR(f.total)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                    {f.numero_pedido ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${
                        estadoBadge[f.estado]
                      }`}
                    >
                      {f.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingDueId === f.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="date"
                          value={editingDueValue}
                          onChange={(e) => setEditingDueValue(e.target.value)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          onClick={() => {
                            setDueDate.mutate(
                              {
                                id: f.id,
                                vencimiento: editingDueValue || null,
                              },
                              { onSuccess: () => setEditingDueId(null) },
                            );
                          }}
                          className="text-emerald-700 hover:text-emerald-900"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                        <button
                          onClick={() => setEditingDueId(null)}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          <XCircle size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingDueId(f.id);
                          setEditingDueValue(f.vencimiento ?? "");
                        }}
                        className="text-slate-600 hover:text-brand-700 flex items-center gap-1"
                      >
                        {f.vencimiento
                          ? new Date(f.vencimiento).toLocaleDateString("es-ES")
                          : "—"}
                        <Edit3 size={12} className="opacity-50" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Abrir PDF"
                        onClick={() => {
                          openFacturaPdf(f.id).catch(() =>
                            alert("No se pudo generar el PDF."),
                          );
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                      >
                        <FileDown size={16} />
                      </button>
                      {f.estado !== "borrador" && (
                        <button
                          title="Enviar al cliente por email"
                          disabled={sendEmail.isPending}
                          onClick={() => {
                            if (
                              !confirm(
                                `¿Enviar la factura ${f.numero} por email al cliente ${
                                  f.cliente?.nombre ?? ""
                                }?`,
                              )
                            ) {
                              return;
                            }
                            sendEmail.mutate(f.id, {
                              onSuccess: (d) =>
                                alert(`Factura enviada a ${d.email}.`),
                              onError: (err) => {
                                const msg =
                                  (
                                    err as {
                                      response?: { data?: { message?: string } };
                                    }
                                  )?.response?.data?.message ??
                                  "No se pudo enviar el email.";
                                alert(msg);
                              },
                            });
                          }}
                          className="p-1.5 rounded hover:bg-brand-50 text-brand-700 disabled:opacity-40"
                        >
                          <Mail size={16} />
                        </button>
                      )}
                      {f.estado !== "pagada" && f.estado !== "anulada" && (
                        <button
                          title="Marcar como pagada"
                          onClick={() => markPaid.mutate(f.id)}
                          className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                        >
                          <CheckCircle2 size={16} />
                        </button>
                      )}
                      <button
                        title="Reenviar a Verifacti"
                        disabled={resendVerifacti.isPending}
                        onClick={() =>
                          resendVerifacti.mutate(f.id, {
                            onSuccess: (r) =>
                              alert(
                                r.ok
                                  ? `Factura ${f.numero} registrada en Verifacti. QR actualizado.`
                                  : `Verifacti no aceptó el envío: ${r.message ?? "error desconocido"}`,
                              ),
                            onError: () =>
                              alert("No se pudo contactar con el servidor para reenviar a Verifacti."),
                          })
                        }
                        className="p-1.5 rounded hover:bg-sky-50 text-sky-700"
                      >
                        <Send size={16} />
                      </button>
                      {f.estado !== "anulada" && (
                        <button
                          title="Anular"
                          onClick={() => {
                            if (confirm("¿Anular esta factura?")) {
                              voidFactura.mutate(f.id);
                            }
                          }}
                          className="p-1.5 rounded hover:bg-amber-50 text-amber-700"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                      {f.tipo !== "rectificativa" && f.estado !== "anulada" && (
                        <button
                          title="Hacer recurrente"
                          disabled={makeRecurring.isPending}
                          onClick={() => {
                            const frec = prompt(
                              `Convertir ${f.numero} en factura recurrente.\n\nFrecuencia (mensual / trimestral / anual):`,
                              "mensual",
                            );
                            if (!frec) return;
                            if (!["mensual", "trimestral", "anual"].includes(frec)) {
                              alert("Frecuencia no válida.");
                              return;
                            }
                            makeRecurring.mutate(
                              { id: f.id, frecuencia: frec as "mensual" | "trimestral" | "anual" },
                              {
                                onSuccess: () =>
                                  alert(
                                    "Plantilla recurrente creada. Actívala en Automatizaciones → «Facturas recurrentes» para que se emita sola.",
                                  ),
                                onError: () => alert("No se pudo crear la plantilla."),
                              },
                            );
                          }}
                          className="p-1.5 rounded hover:bg-violet-50 text-violet-600 disabled:opacity-40"
                        >
                          <Repeat size={16} />
                        </button>
                      )}
                      {f.tipo !== "rectificativa" &&
                        (f.estado === "emitida" || f.estado === "pagada") && (
                          <button
                            title="Emitir rectificativa"
                            disabled={rectify.isPending}
                            onClick={() => {
                              const motivo = prompt(
                                `Se emitirá una factura rectificativa (importes en negativo) de ${f.numero} y la original quedará anulada.\n\nMotivo de la rectificación:`,
                              );
                              if (motivo === null) return;
                              rectify.mutate(
                                { id: f.id, motivo: motivo || undefined },
                                {
                                  onSuccess: (r) =>
                                    alert(`Rectificativa ${r.numero} emitida.`),
                                  onError: (err) =>
                                    alert(
                                      (err as { response?: { data?: { message?: string } } })
                                        ?.response?.data?.message ??
                                        "No se pudo emitir la rectificativa.",
                                    ),
                                },
                              );
                            }}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600 disabled:opacity-40"
                          >
                            <Undo2 size={16} />
                          </button>
                        )}
                      <button
                        title="Eliminar"
                        onClick={() => {
                          if (confirm("Eliminar definitivamente esta factura?")) {
                            deleteFactura.mutate(f.id);
                          }
                        }}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={facturas.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
}
